import React from 'react';
import styled from 'styled-components';
import { lineHeight, theme, toRem } from 'design';
import { IIntegrationApp, IIntegrationConfig, IntegrationType } from 'types';
import { findIntegrationApp, resolveIntegrationSettingRows, toTitleCase } from 'utils';
import { ButtonText } from '../Button';
import { Text } from '../Typography';
import IntegrationSettingRow from './IntegrationSettingRow';

/**
 * Minimal shape the card needs to render an integration's settings. Both the live
 * config (`IIntegration`) and the payment snapshot (`IPOSIntegration`) satisfy it,
 * so either can be passed without transformation.
 */
export interface IIntegrationCardData {
  type: IntegrationType;
  config?: IIntegrationConfig[];
}

export interface IIntegrationCardProps {
  /** The integration's settings — live config or payment snapshot. */
  integration: IIntegrationCardData;
  /** Apps catalog used to resolve setting name/description/value. Passed in so the card stays presentational. */
  apps?: IIntegrationApp[] | null;
  /** Drop settings flagged hidden/deprecated in the catalog (used by the live settings view). */
  hideHiddenSettings?: boolean;
  /** Whether details are expanded on first render. Defaults to true. */
  defaultExpanded?: boolean;
  /** Hide the card's own show/hide toggle and always show the details. Used where an outer control (e.g. the payment set "Settings" accordion) already governs visibility. */
  hideToggle?: boolean;
  dataCy?: string;
}

const Card = styled.section`
  display: flex;
  flex-flow: column nowrap;
  background-color: ${theme.color.neutral[50]};
  border: 1px solid ${theme.color.neutral[200]};
  border-radius: ${toRem(8)};
  padding: ${toRem(theme.space.lg)};
  gap: ${toRem(theme.space.sm)};
`;

const Header = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  gap: ${toRem(theme.space.sm)};
`;

const Vendor = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${toRem(theme.space.sm)};
`;

const VendorLogo = styled.img`
  height: ${toRem(24)};
  width: auto;
  max-width: ${toRem(96)};
  object-fit: contain;
`;

const ToggleButton = styled(ButtonText)`
  margin-left: auto;
  flex-shrink: 0;
  font-size: ${toRem(theme.font.size.xxs)};
  line-height: ${lineHeight(theme.font.size.xxs)};
  font-weight: ${theme.font.weight.medium};
  color: ${theme.color.primary[600]};
  text-decoration: none;
  text-align: right;
`;

const Rows = styled.ul`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
`;

/**
 * Shared presentational card for one POS integration's settings. Resolves the
 * vendor name and each setting row from the apps catalog (deprecated/hidden keys
 * still resolve; unknown keys fall back to the raw key + raw value). The catalog
 * is passed in as a prop so the card stays presentational and reusable wherever
 * POS settings are shown.
 */
const IntegrationCard = ({ integration, apps, hideHiddenSettings = false, defaultExpanded = true, hideToggle = false, dataCy = 'integration-card' }: IIntegrationCardProps) => {
  // When the toggle is hidden `expanded` is never surfaced to the user (see `showDetails`),
  // so seed it to false there to avoid a misleading state and keep `defaultExpanded` meaningful only when a toggle exists.
  const [expanded, setExpanded] = React.useState(!hideToggle && defaultExpanded);

  const app = findIntegrationApp(apps, integration.type);
  const vendorName = app?.name ?? toTitleCase(integration.type);
  const rows = React.useMemo(() => resolveIntegrationSettingRows(app, integration.config, hideHiddenSettings), [app, integration.config, hideHiddenSettings]);

  // Without the toggle there is nothing to collapse the card, so the details always show.
  const showDetails = hideToggle || expanded;

  return (
    <Card data-cy={dataCy}>
      <Header>
        <Vendor>
          {app?.imageUrl && <VendorLogo src={app.imageUrl} alt={vendorName} data-cy="integration-card-logo" />}
          <Text size="small" fontWeight="semibold" data-cy="integration-card-name">
            {vendorName}
          </Text>
        </Vendor>
        {!hideToggle && <ToggleButton label={expanded ? 'Hide Details' : 'Show Details'} onClick={() => setExpanded((prev) => !prev)} data-cy="integration-card-toggle" />}
      </Header>
      {showDetails && (
        <Rows data-cy="integration-card-rows">
          {rows.map((row) => (
            <IntegrationSettingRow key={`${integration.type}_${row.key}`} name={row.name} description={row.description} value={row.value} tooltipId={`${integration.type}-${row.key}`} />
          ))}
        </Rows>
      )}
    </Card>
  );
};

export default IntegrationCard;
